import {
  ChevronDown,
  ChevronUp,
  Download,
  Filter,
  Loader2,
  Plus,
  Search,
  Upload,
  Users,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useSales } from '@/modules/admin/hooks/useSales'
import { bulkUploadCustomers } from '@/modules/customer/api/customer.api'
import { CustomerActions } from '@/modules/customer/components/CustomerActions'
import { EditCustomerModal } from '@/modules/customer/components/EditCustomerModal'
import { useCustomerStore } from '@/modules/customer/store/customer.store'
import { downloadCustomerTemplate } from '@/modules/customer/utils/customerTemplate'
import { AddLeadDialog } from '@/modules/marketing/components/leads/AddLeadDialog'
import { lookupPincode } from '@/shared/api/pincode.api'
import { DataTablePagination } from '@/shared/components/data-table'
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog'
import CustomDateRangePicker from '@/shared/components/ui/CustomDateRangePicker'
import DashboardPeriodFilter from '@/shared/components/ui/DashboardPeriodFilter'
import { toast } from '@/shared/lib/toast'

import CreateQuoteDialog from '../components/quotations/CreateQuoteDialog'

const INITIAL_CUSTOMER_FORM = {
  companyName: '',
  contactPersonName: '',
  contactPersonPhone: '',
  contactPersonEmail: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  pincode: '',
  country: '',
  gst: '',
}

const sanitizeCustomerInput = (field, value) => {
  if (field === 'companyName') {
    return value.replace(/[^a-zA-Z0-9\s.,&'()/-]/g, '')
  }
  if (field === 'contactPersonName') {
    return value.replace(/[^a-zA-Z0-9\s.'-]/g, '')
  }
  if (field === 'contactPersonPhone') {
    return value.replace(/\D/g, '').slice(0, 10)
  }
  if (
    field === 'addressLine1' ||
    field === 'addressLine2' ||
    field === 'city' ||
    field === 'state' ||
    field === 'country'
  ) {
    return value.replace(/[^a-zA-Z0-9\s.,#&'()/-]/g, '')
  }
  if (field === 'pincode') {
    return value.replace(/\D/g, '').slice(0, 6)
  }
  if (field === 'gst') {
    return value
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 15)
      .toUpperCase()
  }
  return value
}

const validateCustomerForm = (data) => {
  if (!data.companyName?.trim()) {
    return 'Company Name is required'
  }
  if (!/^[a-zA-Z0-9\s.,&'()/-]+$/.test(data.companyName.trim())) {
    return 'Company Name must be alphanumeric'
  }
  if (!data.contactPersonName?.trim()) {
    return 'Contact Person Name is required'
  }
  if (!/^[a-zA-Z0-9\s.'-]+$/.test(data.contactPersonName.trim())) {
    return 'Contact Person Name must be alphanumeric'
  }
  if (!data.contactPersonPhone?.trim()) {
    return 'Contact Person Phone is required'
  }
  if (!/^\d{10}$/.test(data.contactPersonPhone.trim())) {
    return 'Phone number must be numeric and exactly 10 digits'
  }
  if (!data.contactPersonEmail?.trim()) {
    return 'Contact Person Email is required'
  }
  if (
    !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i.test(
      data.contactPersonEmail.trim(),
    )
  ) {
    return 'Please enter a valid email address'
  }
  if (!data.addressLine1?.trim()) return 'Address Line 1 is required'
  if (!data.city?.trim()) return 'City is required'
  if (!data.state?.trim()) return 'State is required'
  if (!data.pincode?.trim()) return 'Pincode is required'
  if (!data.country?.trim()) return 'Country is required'
  if (data.gst && data.gst.trim() && !/^[a-zA-Z0-9]+$/.test(data.gst.trim())) {
    return 'GST must be alphanumeric'
  }
  return null
}

export default function AdminCustomersPage() {
  const navigate = useNavigate()
  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false)
  const [isBulkUploading, setIsBulkUploading] = useState(false)
  const bulkFileInputRef = useRef(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const periodParam = searchParams.get('period') || 'all'
  const startDateParam = searchParams.get('startDate') || ''
  const endDateParam = searchParams.get('endDate') || ''
  const salesPersonParam = searchParams.get('salesPerson') || ''

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [period, setPeriod] = useState(periodParam)
  const [salesPerson, setSalesPerson] = useState(salesPersonParam)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [customRange, setCustomRange] = useState({
    startDate: startDateParam,
    endDate: endDateParam,
  })

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPeriod(periodParam)
    setSalesPerson(salesPersonParam)
    setCustomRange({
      startDate: startDateParam,
      endDate: endDateParam,
    })
  }, [periodParam, salesPersonParam, startDateParam, endDateParam])

  const handlePeriodChange = (val) => {
    setPeriod(val)
    const updated = new URLSearchParams(searchParams)
    if (val === 'all') {
      updated.delete('period')
      updated.delete('startDate')
      updated.delete('endDate')
    } else {
      updated.set('period', val)
      if (val !== 'custom') {
        updated.delete('startDate')
        updated.delete('endDate')
      }
    }
    setSearchParams(updated)
  }

  const handleCustomRangeChange = (range) => {
    setCustomRange(range)
    const updated = new URLSearchParams(searchParams)
    updated.set('period', 'custom')
    if (range.startDate) updated.set('startDate', range.startDate)
    if (range.endDate) updated.set('endDate', range.endDate)
    setSearchParams(updated)
  }

  const [customerForm, setCustomerForm] = useState(INITIAL_CUSTOMER_FORM)
  const [isLookingUpPincode, setIsLookingUpPincode] = useState(false)

  // Per-customer actions state
  const [isCreateLeadOpen, setIsCreateLeadOpen] = useState(false)
  const [isCreateQuoteOpen, setIsCreateQuoteOpen] = useState(false)
  const [selectedCustomerForEdit, setSelectedCustomerForEdit] = useState(null)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [customerToDelete, setCustomerToDelete] = useState(null)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const {
    customers,
    meta,
    isLoading,
    isSubmitting,
    fetchCustomers,
    createCustomer,
    updateCustomer,
    deleteCustomer,
  } = useCustomerStore()

  const { sales, fetchSales } = useSales()

  useEffect(() => {
    fetchSales({
      page: 1,
      limit: 100,
    })
  }, [fetchSales])

  const filterKey = `${search}|${period}|${salesPerson}|${customRange.startDate}|${customRange.endDate}`
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey)

  if (prevFilterKey !== filterKey) {
    setPrevFilterKey(filterKey)
    setPage(1)
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCustomers({
        page,
        limit: 5,
        search,
        period,
        salesPerson: salesPerson || undefined,
        startDate: period === 'custom' ? customRange.startDate : undefined,
        endDate: period === 'custom' ? customRange.endDate : undefined,
      })
    }, 400)

    return () => clearTimeout(timer)
  }, [page, search, period, salesPerson, customRange, fetchCustomers])

  const handleCustomerFormChange = (field, value) => {
    setCustomerForm((prev) => ({
      ...prev,
      [field]: sanitizeCustomerInput(field, value),
    }))
  }

  const handlePincodeChange = async (e) => {
    const rawVal = e.target.value
    const pin = rawVal.replace(/\D/g, '').slice(0, 6)

    setCustomerForm((prev) => ({
      ...prev,
      pincode: pin,
    }))

    if (pin.length === 6) {
      try {
        setIsLookingUpPincode(true)
        const details = await lookupPincode(pin)
        if (details) {
          setCustomerForm((prev) => ({
            ...prev,
            pincode: pin,
            city: details.city || prev.city,
            state: details.state || prev.state,
            country: details.country || prev.country || 'India',
          }))
          toast.success(
            `Location auto-filled: ${details.city}, ${details.state}`,
          )
        } else {
          toast.error('No location found for this pincode')
        }
      } catch (err) {
        console.error('Pincode lookup error:', err)
        toast.error('Failed to fetch location details for this pincode')
      } finally {
        setIsLookingUpPincode(false)
      }
    }
  }

  const handleCloseCustomerModal = () => {
    if (isSubmitting) return
    setCustomerForm(INITIAL_CUSTOMER_FORM)
    setIsAddCustomerOpen(false)
  }

  const handleBulkUpload = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    setIsBulkUploading(true)
    try {
      const result = await bulkUploadCustomers(file)
      const data = result?.data
      const count = data?.count ?? 0
      if (count > 0) {
        toast.success(`Successfully uploaded ${count} customer(s)!`)
      }
      if (data?.errors?.length) {
        const first = data.errors.slice(0, 3)
        first.forEach((e) => toast.error(`Row ${e.row}: ${e.message}`))
        if (data.errors.length > 3) {
          toast.error(`…and ${data.errors.length - 3} more row error(s).`)
        }
      }
      fetchCustomers({
        page: 1,
        search,
        period,
        salesPerson: salesPerson || undefined,
      })
      if (count > 0) setIsAddCustomerOpen(false)
    } catch (err) {
      toast.error(err?.message || 'Bulk upload failed')
    } finally {
      setIsBulkUploading(false)
      event.target.value = ''
    }
  }

  const handleCreateCustomer = async () => {
    const errorMsg = validateCustomerForm(customerForm)
    if (errorMsg) {
      toast.error(errorMsg)
      return
    }

    try {
      await createCustomer(customerForm)
      setCustomerForm(INITIAL_CUSTOMER_FORM)
      setIsAddCustomerOpen(false)
      fetchCustomers({
        page: 1,
        search,
        period,
        salesPerson: salesPerson || undefined,
        startDate: period === 'custom' ? customRange.startDate : undefined,
        endDate: period === 'custom' ? customRange.endDate : undefined,
      })
    } catch {
      // Error is already handled by the customer store
    }
  }

  const handleOpenCreateLead = () => {
    setIsCreateLeadOpen(true)
  }

  const handleCreateQuotation = (customer) => {
    if (customer?._id || customer?.id) {
      const id = customer._id || customer.id
      navigate(`/admin/leads/create-quotation/cust_${id}`, {
        state: { customer },
      })
      return
    }
    setIsCreateQuoteOpen(true)
  }

  const handleOpenEdit = (customer) => {
    setSelectedCustomerForEdit(customer)
    setIsEditOpen(true)
  }

  const handleOpenDelete = (customer) => {
    setCustomerToDelete(customer)
    setIsDeleteOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!customerToDelete) return
    try {
      setIsDeleting(true)
      await deleteCustomer(customerToDelete._id)
      setIsDeleteOpen(false)
      setCustomerToDelete(null)
    } catch {
      // Error handled by store
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="rounded-2xl sm:rounded-3xl border bg-white px-4 py-4 sm:px-7 sm:py-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-slate-900">
              Customers
            </h1>

            <p className="mt-1 text-sm text-slate-500">Manage customers.</p>
          </div>

          <button
            type="button"
            onClick={() => setIsAddCustomerOpen(true)}
            className="flex items-center justify-center gap-2.5 rounded-full bg-slate-950 px-6 py-3.5 text-sm font-semibold text-white w-full sm:w-auto"
          >
            <Plus className="h-5 w-5" />
            Add Customer
          </button>
        </div>
      </div>

      {/* Customer Table */}
      <div className="overflow-hidden rounded-3xl border bg-white shadow-sm">
        {/* Search / Filter */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b p-3.5 sm:px-5 sm:py-4">
          <div className="flex w-full items-center gap-3 rounded-full bg-slate-100 px-4 py-3 sm:px-5 sm:py-3.5 lg:w-[45%]">
            <Search className="h-5 w-5 text-slate-500 shrink-0" />

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by company, contact person, email or phone..."
              className="w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-500"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Salesperson Filter */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsFilterOpen((prev) => !prev)}
                className="flex h-10 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
              >
                <Filter className="size-3.5 text-slate-500" />

                {salesPerson
                  ? sales.find((person) => person._id === salesPerson)?.name ||
                    'Salesperson'
                  : 'All Salespersons'}

                {isFilterOpen ? (
                  <ChevronUp className="size-3 text-slate-400" />
                ) : (
                  <ChevronDown className="size-3 text-slate-400" />
                )}
              </button>

              {isFilterOpen && (
                <div className="absolute right-0 top-full z-50 mt-1 max-h-60 w-52 overflow-y-auto rounded-xl border border-slate-100 bg-white py-1.5 shadow-xl">
                  <button
                    type="button"
                    onClick={() => {
                      setSalesPerson('')
                      setIsFilterOpen(false)
                      const updated = new URLSearchParams(searchParams)
                      updated.delete('salesPerson')
                      setSearchParams(updated)
                    }}
                    className={`w-full px-4 py-2.5 text-left text-xs font-medium hover:bg-slate-50 ${
                      !salesPerson
                        ? 'bg-slate-50 text-slate-900'
                        : 'text-slate-600'
                    }`}
                  >
                    All Salespersons
                  </button>

                  {sales.map((person) => (
                    <button
                      key={person._id}
                      type="button"
                      onClick={() => {
                        setSalesPerson(person._id)
                        setIsFilterOpen(false)
                        const updated = new URLSearchParams(searchParams)
                        updated.set('salesPerson', person._id)
                        setSearchParams(updated)
                      }}
                      className={`w-full px-4 py-2.5 text-left text-xs font-medium hover:bg-slate-50 ${
                        salesPerson === person._id
                          ? 'bg-slate-50 text-slate-900'
                          : 'text-slate-600'
                      }`}
                    >
                      {person.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Custom Date Range */}
            {period === 'custom' && (
              <CustomDateRangePicker
                startDate={customRange.startDate}
                endDate={customRange.endDate}
                onChange={handleCustomRangeChange}
              />
            )}

            {/* Period Filter */}
            <DashboardPeriodFilter
              value={period}
              onChange={handlePeriodChange}
            />

            {/* Customer Count */}
            <div className="flex h-10 items-center gap-2 rounded-full border bg-white px-4 text-xs font-medium text-slate-700 shadow-sm">
              <Users className="size-4 text-slate-500" />
              <span>
                {customers.length} of {meta?.total ?? 0}
              </span>
            </div>
          </div>
        </div>
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1200px]">
            <thead>
              <tr className="border-b text-left">
                <th className="px-5 py-5 text-xs font-bold uppercase tracking-wide text-slate-900">
                  Company Name
                </th>

                <th className="px-5 py-5 text-xs font-bold uppercase tracking-wide text-slate-900">
                  Contact Person
                </th>

                <th className="px-5 py-5 text-xs font-bold uppercase tracking-wide text-slate-900">
                  Phone Number
                </th>

                <th className="px-5 py-5 text-xs font-bold uppercase tracking-wide text-slate-900">
                  Email ID
                </th>

                <th className="px-5 py-5 text-xs font-bold uppercase tracking-wide text-slate-900">
                  Address
                </th>

                <th className="px-5 py-5 text-xs font-bold uppercase tracking-wide text-slate-900">
                  GST
                </th>

                <th className="px-5 py-5 text-xs font-bold uppercase tracking-wide text-slate-900">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-5 py-20 text-center text-sm text-slate-400"
                  >
                    Loading customers...
                  </td>
                </tr>
              ) : customers.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-5 py-20 text-center text-sm text-slate-400"
                  >
                    No customers found
                  </td>
                </tr>
              ) : (
                customers.map((customer) => (
                  <tr key={customer._id} className="border-b last:border-b-0">
                    <td className="px-5 py-5 text-sm font-semibold text-slate-900">
                      {customer.companyName}
                    </td>

                    <td className="px-5 py-5 text-sm text-slate-700">
                      {customer.contactPersonName}
                    </td>

                    <td className="px-5 py-5 text-sm text-slate-700">
                      {customer.contactPersonPhone}
                    </td>

                    <td className="px-5 py-5 text-sm text-slate-700">
                      {customer.contactPersonEmail}
                    </td>

                    <td className="px-5 py-5 text-sm text-slate-700">
                      {[
                        customer.addressLine1,
                        customer.addressLine2,
                        customer.city,
                        customer.state,
                        customer.pincode,
                        customer.country,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </td>

                    <td className="px-5 py-5 text-sm text-slate-700">
                      {customer.gst || '-'}
                    </td>

                    <td className="px-5 py-5 text-sm">
                      <CustomerActions
                        customer={customer}
                        onCreateLead={handleOpenCreateLead}
                        onCreateQuotation={handleCreateQuotation}
                        onEdit={handleOpenEdit}
                        onDelete={handleOpenDelete}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && (
          <DataTablePagination
            page={meta?.page ?? page}
            pageSize={meta?.limit ?? 5}
            total={meta?.total ?? 0}
            onPageChange={(newPage) => setPage(newPage)}
          />
        )}
      </div>

      {/* Add Customer Modal */}
      {isAddCustomerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-4">
          <div className="w-full max-w-2xl rounded-2xl sm:rounded-3xl bg-white p-4 sm:p-7 shadow-xl max-h-[92vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">
                  Add Customer
                </h2>

                <p className="mt-1 text-sm text-slate-500">
                  Add a new customer.
                </p>
              </div>

              <button
                type="button"
                onClick={handleCloseCustomerModal}
                disabled={isSubmitting}
                className="rounded-full bg-slate-950 px-6 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Close
              </button>
            </div>

            {/* Bulk Upload Section */}
            <div className="mt-5 rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold text-slate-600 mb-3">
                Bulk Upload via Excel / CSV
              </p>
              <div className="flex flex-col sm:flex-row gap-2.5">
                {/* Hidden file input */}
                <input
                  ref={bulkFileInputRef}
                  type="file"
                  accept=".csv,.xls,.xlsx"
                  className="hidden"
                  onChange={handleBulkUpload}
                />
                <button
                  type="button"
                  disabled={isBulkUploading}
                  onClick={() => bulkFileInputRef.current?.click()}
                  className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50 w-full sm:w-auto"
                >
                  {isBulkUploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 text-slate-500" />
                  )}
                  {isBulkUploading ? 'Uploading...' : 'Upload Excel / CSV'}
                </button>
                <button
                  type="button"
                  onClick={downloadCustomerTemplate}
                  className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 w-full sm:w-auto"
                >
                  <Download className="h-4 w-4 text-slate-500" />
                  Download Template
                </button>
              </div>
              <p className="mt-2 text-[11px] text-slate-400">
                Upload multiple customers at once. Download the template to see
                the required format.
              </p>
            </div>

            {/* Divider */}
            <div className="mt-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-xs font-medium text-slate-400">
                or add manually
              </span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>

            {/* Customer Form */}
            <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
              {/* Company Name */}
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Company Name
                </label>

                <input
                  type="text"
                  value={customerForm.companyName}
                  onChange={(e) =>
                    handleCustomerFormChange('companyName', e.target.value)
                  }
                  placeholder="Enter company name"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
                />
              </div>

              {/* Contact Person Name */}
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Contact Person Name
                </label>

                <input
                  type="text"
                  value={customerForm.contactPersonName}
                  onChange={(e) =>
                    handleCustomerFormChange(
                      'contactPersonName',
                      e.target.value,
                    )
                  }
                  placeholder="Enter contact person name"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
                />
              </div>

              {/* Contact Person Phone Number */}
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Contact Person Phone Number
                </label>

                <input
                  type="tel"
                  maxLength={10}
                  value={customerForm.contactPersonPhone}
                  onChange={(e) =>
                    handleCustomerFormChange(
                      'contactPersonPhone',
                      e.target.value,
                    )
                  }
                  placeholder="Enter phone number"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
                />
              </div>

              {/* Contact Person Email ID */}
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Contact Person Email ID
                </label>

                <input
                  type="email"
                  value={customerForm.contactPersonEmail}
                  onChange={(e) =>
                    handleCustomerFormChange(
                      'contactPersonEmail',
                      e.target.value,
                    )
                  }
                  placeholder="Enter email address"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
                />
              </div>

              {/* Address Line 1 */}
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Address Line 1
                </label>
                <input
                  type="text"
                  value={customerForm.addressLine1}
                  onChange={(e) =>
                    handleCustomerFormChange('addressLine1', e.target.value)
                  }
                  placeholder="Enter address line 1"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
                />
              </div>

              {/* Address Line 2 */}
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Address Line 2
                  <span className="ml-1 text-xs font-normal text-slate-400">
                    (Optional)
                  </span>
                </label>
                <input
                  type="text"
                  value={customerForm.addressLine2}
                  onChange={(e) =>
                    handleCustomerFormChange('addressLine2', e.target.value)
                  }
                  placeholder="Enter address line 2"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
                />
              </div>

              {/* City */}
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  City
                </label>
                <input
                  type="text"
                  value={customerForm.city}
                  onChange={(e) =>
                    handleCustomerFormChange('city', e.target.value)
                  }
                  placeholder="Enter city"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
                />
              </div>

              {/* State */}
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  State
                </label>
                <input
                  type="text"
                  value={customerForm.state}
                  onChange={(e) =>
                    handleCustomerFormChange('state', e.target.value)
                  }
                  placeholder="Enter state"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
                />
              </div>

              {/* Pincode */}
              <div>
                <label className="mb-2 flex items-center justify-between text-sm font-medium text-slate-700">
                  <span>Pincode</span>
                  {isLookingUpPincode && (
                    <span className="flex items-center gap-1 text-xs font-normal text-blue-600">
                      <Loader2 className="size-3 animate-spin" />
                      Fetching...
                    </span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    maxLength={6}
                    value={customerForm.pincode}
                    onChange={handlePincodeChange}
                    placeholder="Enter 6-digit pincode"
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
                  />
                  {isLookingUpPincode && (
                    <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
                      <Loader2 className="size-4 animate-spin text-slate-400" />
                    </div>
                  )}
                </div>
              </div>

              {/* Country */}
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Country
                </label>
                <input
                  type="text"
                  value={customerForm.country}
                  onChange={(e) =>
                    handleCustomerFormChange('country', e.target.value)
                  }
                  placeholder="Enter country"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
                />
              </div>

              {/* GST */}
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  GST
                  <span className="ml-1 text-xs font-normal text-slate-400">
                    (Optional)
                  </span>
                </label>

                <input
                  type="text"
                  maxLength={15}
                  value={customerForm.gst}
                  onChange={(e) =>
                    handleCustomerFormChange('gst', e.target.value)
                  }
                  placeholder="Enter GST number if available"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm uppercase outline-none focus:border-slate-400"
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="mt-7 flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5 sm:gap-3">
              <button
                type="button"
                onClick={handleCloseCustomerModal}
                disabled={isSubmitting}
                className="w-full sm:w-auto rounded-full border border-slate-200 px-6 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleCreateCustomer}
                disabled={isSubmitting}
                className="w-full sm:w-auto rounded-full bg-slate-950 px-7 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? 'Adding...' : 'Add Customer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Lead Modal */}
      <AddLeadDialog
        open={isCreateLeadOpen}
        onOpenChange={setIsCreateLeadOpen}
        lead={null}
        module="admin"
        onSuccess={() => {
          toast.success('Lead created successfully')
        }}
      />

      {/* Select Lead & Create Quotation Dialog */}
      <CreateQuoteDialog
        open={isCreateQuoteOpen}
        onOpenChange={setIsCreateQuoteOpen}
      />

      {/* Edit Customer Modal */}
      <EditCustomerModal
        open={isEditOpen}
        onOpenChange={(open) => {
          setIsEditOpen(open)
          if (!open) setSelectedCustomerForEdit(null)
        }}
        customer={selectedCustomerForEdit}
        onUpdate={updateCustomer}
      />

      {/* Delete Customer Confirm Dialog */}
      <ConfirmDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        title="Delete Customer"
        description={`Are you sure you want to delete customer "${customerToDelete?.companyName}"? This action cannot be undone.`}
        confirmText="Delete"
        loading={isDeleting}
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}
